import {
  Component,
  ElementRef,
  Input,
  ViewChild,
  Renderer2,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { SearchIcd10CodeComponent } from '../search-icd10-code/search-icd10-code.component';

@Component({
  selector: 'app-icd10-input',
  templateUrl: './icd10-input.component.html',
  styleUrls: ['./icd10-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: Icd10InputComponent,
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: Icd10InputComponent,
    },
  ],
})
export class Icd10InputComponent {
  @ViewChild('parentContainer')
  parentContainer!: ElementRef;

  private formId: string = '';
  public showPlaceHolder = false;

  @Input()
  title = 'Select ICD10 codes...';

  @Input()
  isDisabled = false;

  @Input()
  public values: Array<string> = [];

  constructor(
    private renderer: Renderer2,
    private el: ElementRef,
    private modalService: NgbModal
  ) {
    this.formId =
      'item_' +
      Math.floor(Math.random() * (9999999 - 1111111) + 1111111).toString();
  }

  ngOnInit(): void { }

  ngAfterViewInit() {
    if (!this.touched) {
      this.renderTags(false);
    }
  }

  searchIcd10Code() {
    const modalRef = this.modalService.open(SearchIcd10CodeComponent, {
      centered: true,
      size: 'xl',
      backdrop: 'static',
    });
    modalRef.result.then((icd10Code: any) => {
      if (icd10Code) {
        this.values.push(icd10Code.code);
        this.renderTags(true);
      }
    });
  }

  private renderTags(shoudFocus: boolean) {
    let tagsHtml = '';
    this.values = Array.from(new Set(this.values));
    this.onChange(this.values);
    this.values.forEach((tag) => {
      tagsHtml += `<div><span class="selected-tag">${tag}<i (click)="onRemoveTag('${tag}')" class="bi bi-x fw-bold"></i></span></div>`;
    });

    const parentContainer = this.el.nativeElement.querySelector(
      '.parent-tag-container'
    );
    this.renderer.setProperty(parentContainer, 'innerHTML', tagsHtml);

    const removeIcons = parentContainer.querySelectorAll('.bi-x');
    removeIcons.forEach((icon: any) => {
      this.renderer.listen(icon, 'click', (event) => this.onRemoveTag(event));
    });

    if (this.isDisabled == false) {
      this.appendEditableForm(shoudFocus);
    }

    this.refreshShowPlaceholder();
  }

  appendEditableForm(shoudFocus: boolean) {
    const contentEditable = this.renderer.createElement('div');
    this.renderer.setAttribute(contentEditable, 'contenteditable', 'true');
    this.renderer.setAttribute(contentEditable, 'id', this.formId);
    this.renderer.addClass(contentEditable, 'contenteditable');
    this.renderer.listen(contentEditable, 'paste', () => this.onInputChanged());
    this.renderer.listen(contentEditable, 'keyup', () => this.onInputChanged());
    this.renderer.listen(contentEditable, 'keydown', (event: KeyboardEvent) => {
      this.onTouched();
      if (event.key === 'Backspace') {
        this.deleteTagIfNecessary();
      }
    });
    this.renderer.listen(contentEditable, 'keyup', (event: KeyboardEvent) => {
      this.onTouched();
      if (
        event.key === 'Tab' ||
        event.key === 'Enter' ||
        event.key === ';' ||
        event.key === 'ArrowRight'
      ) {
        this.addNewTag();
      }
    });

    this.renderer.listen(contentEditable, 'blur', () => {
      this.addNewTag();
    });

    this.renderer.appendChild(
      this.parentContainer.nativeElement,
      contentEditable
    );

    this.renderer.appendChild(
      this.parentContainer.nativeElement,
      contentEditable
    );

    if (shoudFocus) {
      contentEditable.focus();
    }
  }

  onChange = (string: Array<string>) => { };

  onTouched = () => { };

  touched = false;

  disabled = false;

  onRemoveTag(event: Event) {
    if (this.isDisabled == false) {
      const tagToRemove = (
        event.target as HTMLElement
      ).parentNode?.textContent?.trim();
      this.values = this.values.filter((el) => el != tagToRemove);
      this.renderTags(true);
    }
  }

  onItemClick(val: string) {
    this.markAsTouched();
    if (!this.disabled) {
    }
  }

  private updateTagsArray(inputString: string) {
    const cleanedString = inputString.replace(/[^a-zA-Z0-9,\.]/g, '');
    const stringArray = cleanedString.split(',');
    this.values.push(...stringArray.filter((el) => el.trim().length > 0));
    return stringArray;
  }

  onInputChanged() {
    const value = this.el.nativeElement.querySelector(
      '#' + this.formId
    )?.innerText;

    if (!!value) {
      if (value.indexOf(',') >= 0) {
        this.updateTagsArray(value!);
        document.querySelector('#' + this.formId)!.innerHTML = '';
        this.renderTags(true);
      }
    }
    this.refreshShowPlaceholder();
  }

  refreshShowPlaceholder() {
    const value = this.el.nativeElement.querySelector(
      '#' + this.formId
    )?.innerText;
    if ((value?.length ?? 0) > 0 || this.values.length > 0) {
      this.showPlaceHolder = false;
    } else {
      this.showPlaceHolder = true;
    }
  }

  deleteTagIfNecessary() {
    const value = this.el.nativeElement.querySelector(
      '#' + this.formId
    )?.innerText;
    if ((value?.length ?? 0) == 0) {
      this.values.pop();
      this.renderTags(true);
    }
  }

  addNewTag() {
    const value = this.el.nativeElement.querySelector(
      '#' + this.formId
    )?.innerText;
    if ((value?.trim().length ?? 0) > 0 && value != '&nbsp;') {
      this.values.push(value!.replace(/[^a-zA-Z0-9,\.]/g, ''));
      this.renderTags(true);
    }
  }

  writeValue(val?: Array<string>) {
    if (!!val) {
      this.values = val;
      this.renderTags(false);
    }
    this.refreshShowPlaceholder();
  }

  registerOnChange(onChange: any) {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: any) {
    this.onTouched = onTouched;
  }

  markAsTouched() {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  setDisabledState(disabled: boolean) {
    this.disabled = disabled;
  }

  validate(control: AbstractControl): ValidationErrors | null {
    return null;
  }
}
